/**
 * Pure helpers for the Events calendar view.
 *
 * Key decisions:
 *  - Week starts on Monday by default (matches the mockup's weekday header).
 *  - Month-grid buckets are keyed by the viewer's local calendar day so
 *    `groupEventsByDay()` and `getCellKey()` use the same date basis.
 *  - The grid is always 6 weeks (42 cells) so the layout doesn't jitter
 *    between months with 28 vs 31 days.
 */

export const DAYS_PER_WEEK = 7;
export const WEEKS_PER_MONTH_GRID = 6;
export const CELLS_PER_MONTH_GRID = DAYS_PER_WEEK * WEEKS_PER_MONTH_GRID;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(date) {
    const copy = new Date(date.getTime());
    copy.setHours(0, 0, 0, 0);
    return copy;
}

function addDays(date, days) {
    const copy = new Date(date.getTime());
    copy.setDate(copy.getDate() + days);
    return copy;
}

function addUtcDays(date, days) {
    return new Date(date.getTime() + days * ONE_DAY_MS);
}

function safeTimeValue(value) {
    if (!value) return NaN;
    const ts = new Date(value).getTime();
    return Number.isFinite(ts) ? ts : NaN;
}

function safeStartTime(event) {
    return safeTimeValue(event?.startAt);
}

function safeEndTime(event) {
    const end = safeTimeValue(event?.endAt);
    return Number.isNaN(end) ? safeStartTime(event) : end;
}

function normalizeStatus(status) {
    return typeof status === 'string' ? status.trim().toUpperCase() : '';
}

function isUpcomingCalendarEvent(event) {
    const status = normalizeStatus(event?.status);
    return status !== 'DRAFT' && status !== 'CANCELLED' && status !== 'COMPLETED';
}

function toReferenceDate(value) {
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

function getWindowAnchorTime(event, referenceMs) {
    const start = safeStartTime(event);
    const end = safeEndTime(event);
    if (Number.isNaN(start) && Number.isNaN(end)) {
        return NaN;
    }
    if (Number.isNaN(start)) {
        return Math.max(end, referenceMs);
    }
    if (!Number.isNaN(end) && start < referenceMs && end >= referenceMs) {
        return referenceMs;
    }
    return start;
}

function compareByUpcomingWindow(a, b, referenceMs) {
    const aAnchor = getWindowAnchorTime(a, referenceMs);
    const bAnchor = getWindowAnchorTime(b, referenceMs);
    if (Number.isNaN(aAnchor) && Number.isNaN(bAnchor)) return 0;
    if (Number.isNaN(aAnchor)) return 1;
    if (Number.isNaN(bAnchor)) return -1;
    if (aAnchor !== bAnchor) {
        return aAnchor - bAnchor;
    }

    const aStart = safeStartTime(a);
    const bStart = safeStartTime(b);
    if (Number.isNaN(aStart) && Number.isNaN(bStart)) return 0;
    if (Number.isNaN(aStart)) return 1;
    if (Number.isNaN(bStart)) return -1;
    return aStart - bStart;
}

function sameYMD(a, b) {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

/**
 * Compute the first day that should appear in the month grid: the most recent
 * `weekStartsOn` on or before the 1st of the month.
 */
export function getGridStart(monthDate, weekStartsOn = 1) {
    const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const dayOfWeek = firstOfMonth.getDay(); // 0 = Sunday
    const offset = (dayOfWeek - weekStartsOn + DAYS_PER_WEEK) % DAYS_PER_WEEK;
    return addDays(firstOfMonth, -offset);
}

/**
 * Returns a 6-row × 7-col matrix of descriptor objects, always 42 cells.
 */
export function getMonthGrid(monthDate, weekStartsOn = 1, today = new Date()) {
    const targetMonth = monthDate.getMonth();
    const targetYear = monthDate.getFullYear();
    const gridStart = getGridStart(monthDate, weekStartsOn);
    const todayStart = startOfDay(today);
    const rows = [];

    for (let week = 0; week < WEEKS_PER_MONTH_GRID; week += 1) {
        const row = [];
        for (let day = 0; day < DAYS_PER_WEEK; day += 1) {
            const cellDate = addDays(gridStart, week * DAYS_PER_WEEK + day);
            const isCurrentMonth = cellDate.getMonth() === targetMonth && cellDate.getFullYear() === targetYear;
            row.push({
                date: cellDate,
                isCurrentMonth,
                isToday: sameYMD(cellDate, todayStart),
            });
        }
        rows.push(row);
    }

    return rows;
}

/**
 * Return YYYY-MM-DD for a given date, optionally in a specific IANA timezone.
 * Uses Intl to avoid cross-midnight bucketing bugs.
 */
export function toCalendarKey(date, timezone) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '';
    }
    try {
        const formatter = new Intl.DateTimeFormat('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: timezone || undefined,
        });
        // en-CA yields YYYY-MM-DD natively
        return formatter.format(date);
    } catch (error) {
        // Invalid timezone — fall back to local
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
}

function isExactLocalMidnight(date, timezone) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return false;
    }

    try {
        const formatter = new Intl.DateTimeFormat('en-CA', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
            timeZone: timezone || undefined,
        });
        const parts = formatter.formatToParts(date);
        const hour = Number.parseInt(parts.find((part) => part.type === 'hour')?.value ?? '', 10);
        const minute = Number.parseInt(parts.find((part) => part.type === 'minute')?.value ?? '', 10);
        const second = Number.parseInt(parts.find((part) => part.type === 'second')?.value ?? '', 10);
        return hour === 0 && minute === 0 && second === 0;
    } catch (error) {
        return date.getHours() === 0
            && date.getMinutes() === 0
            && date.getSeconds() === 0
            && date.getMilliseconds() === 0;
    }
}

/**
 * Resolve the viewer-local calendar day range that an event occupies in the
 * month grid. These keys must stay aligned with `getCellKey()` because the
 * grid renders and looks up cells in the viewer's local timezone.
 */
export function resolveViewerCalendarRangeKeys(event) {
    const startDate = event?.startAt ? new Date(event.startAt) : null;
    const validStart = startDate instanceof Date && !Number.isNaN(startDate.getTime()) ? startDate : null;
    const endDate = event?.endAt ? new Date(event.endAt) : null;
    const validEnd = endDate instanceof Date && !Number.isNaN(endDate.getTime()) ? endDate : null;
    const effectiveEnd = validEnd
        ? (isExactLocalMidnight(validEnd) ? new Date(validEnd.getTime() - 1) : validEnd)
        : validStart;

    const startKey = validStart ? getCellKey(validStart) : '';
    const endKey = effectiveEnd ? getCellKey(effectiveEnd) : startKey;

    return {
        startKey,
        endKey,
    };
}

/**
 * Group events by the viewer-local calendar day shown in the month grid.
 * Returns a Map keyed by YYYY-MM-DD → Event[].
 */
export function groupEventsByDay(events) {
    const map = new Map();
    if (!Array.isArray(events)) {
        return map;
    }

    events.forEach((event) => {
        if (!event?.startAt) {
            return;
        }
        const { startKey, endKey } = resolveViewerCalendarRangeKeys(event);
        if (!startKey) {
            return;
        }

        const [startYear, startMonth, startDay] = startKey.split('-').map((value) => Number.parseInt(value, 10));
        const [endYear, endMonth, endDay] = endKey.split('-').map((value) => Number.parseInt(value, 10));
        if (![startYear, startMonth, startDay, endYear, endMonth, endDay].every(Number.isFinite)) {
            return;
        }

        let cursor = new Date(Date.UTC(startYear, startMonth - 1, startDay));
        const last = new Date(Date.UTC(endYear, endMonth - 1, endDay));
        const finalCursor = cursor.getTime() <= last.getTime() ? last : cursor;

        while (cursor.getTime() <= finalCursor.getTime()) {
            const key = `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, '0')}-${String(cursor.getUTCDate()).padStart(2, '0')}`;
            const bucket = map.get(key);
            if (bucket) {
                bucket.push(event);
            } else {
                map.set(key, [event]);
            }
            cursor = addUtcDays(cursor, 1);
        }
    });

    // Within each bucket, sort ascending by startAt
    map.forEach((bucket) => {
        bucket.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    });

    return map;
}

/**
 * Cell-key helper to look up events for a given grid cell using its date.
 * Callers pass the day's *local* date; we derive the YYYY-MM-DD directly
 * (no timezone — the grid is rendered in the viewer's locale).
 */
export function getCellKey(cellDate) {
    if (!(cellDate instanceof Date) || Number.isNaN(cellDate.getTime())) {
        return '';
    }
    const y = cellDate.getFullYear();
    const m = String(cellDate.getMonth() + 1).padStart(2, '0');
    const d = String(cellDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/**
 * Pick the next active/upcoming events from `events`.
 *
 * An event remains eligible until its end time passes, so currently running
 * events stay visible in the side rail instead of disappearing at `startAt`.
 * Cancelled/completed items are excluded even if their scheduled end is still
 * in the future.
 * Returns at most `count` events sorted by the active window anchor.
 */
export function getUpcomingEvents(events, fromDate = new Date(), count = 3) {
    if (!Array.isArray(events) || events.length === 0) {
        return [];
    }
    const referenceDate = toReferenceDate(fromDate);
    const from = referenceDate.getTime();

    return events
        .filter((event) => {
            if (!isUpcomingCalendarEvent(event)) {
                return false;
            }
            const end = safeEndTime(event);
            return Number.isFinite(end) && end >= from;
        })
        .sort((a, b) => compareByUpcomingWindow(a, b, from))
        .slice(0, count);
}

/**
 * Return events whose active interval overlaps the next `daysAhead` calendar
 * days from `fromDate` (inclusive of the start, exclusive of the end).
 *
 * This keeps multi-day and already-running events in the weekly rail until
 * they actually end, but drops cancelled/completed items and anything whose
 * active interval already ended before the current moment. The upper bound uses
 * the same fixed `now + daysAhead * 24h` definition as `partitionEventsBySection`,
 * so list/grid "this week" and the calendar side rail stay aligned even across
 * DST transitions. Sorted by the same active-window ordering as
 * {@link getUpcomingEvents}.
 */
export function getEventsInNextDays(events, fromDate = new Date(), daysAhead = 7) {
    if (!Array.isArray(events) || events.length === 0) {
        return [];
    }
    const referenceDate = toReferenceDate(fromDate);
    const from = referenceDate.getTime();
    const to = addUtcDays(referenceDate, daysAhead).getTime();

    return events
        .filter((event) => {
            if (!isUpcomingCalendarEvent(event)) {
                return false;
            }
            const start = safeStartTime(event);
            const end = safeEndTime(event);
            if (Number.isNaN(start) && Number.isNaN(end)) {
                return false;
            }
            const intervalStart = Number.isNaN(start) ? end : start;
            const intervalEnd = Number.isNaN(end) ? start : end;
            return intervalEnd >= from && intervalStart < to;
        })
        .sort((a, b) => compareByUpcomingWindow(a, b, from));
}
